import React, { useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AdminPanelSettingsOutlined as AdminIcon,
  AnalyticsOutlined as AnalyticsIcon,
  AttachMoneyOutlined as CostsIcon,
  BusinessOutlined as BusinessIcon,
  DeleteOutlineOutlined as TerminationIcon,
  EmailOutlined as EmailIcon,
  FlagOutlined as FlagIcon,
  GroupsOutlined as AudienceIcon,
  LanguageOutlined as WhitelabelIcon,
  NotificationsOutlined as NotificationsIcon,
  PeopleOutline as UsersIcon,
} from "@mui/icons-material";
import AdminAnalytics from "./AdminAnalytics";
import AudienceManagement from "./AudienceManagement";
import CompanyManagement from "./CompanyManagement";
import CompanyTerminations from "./CompanyTerminations";
import CostManagement from "./CostManagement";
import FeatureFlagManagement from "./FeatureFlagManagement";
import MailboxOrderManagement from "./MailboxOrderManagement";
import NotificationManagement from "./NotificationManagement";
import UserManagement from "./UserManagement";
import WhitelabelManagement from "./WhitelabelManagement";

interface AdminPageProps {
  getToken: () => Promise<string>;
  currentCompanyId: string;
  initialSection?: string;
  isSuperUser?: boolean;
}

type AdminSection = "analytics" | "costs" | "users" | "companies" | "terminations" | "whitelabels" | "audiences" | "mailbox-orders" | "notifications" | "feature-flags";

const AdminPage: React.FC<AdminPageProps> = ({
  getToken,
  currentCompanyId,
  initialSection,
  isSuperUser = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeSection, setActiveSection] = useState<AdminSection>(
    (initialSection as AdminSection) || "analytics"
  );

  // Don't render anything if user is not a super user
  if (!isSuperUser) {
    return null;
  }

  const menuItems: { id: AdminSection; label: string; icon: React.ReactNode }[] = [
    {
      id: "analytics",
      label: "Analytics",
      icon: <AnalyticsIcon />,
    },
    {
      id: "costs",
      label: "Costs",
      icon: <CostsIcon />,
    },
    {
      id: "users",
      label: "Users",
      icon: <UsersIcon />,
    },
    {
      id: "companies",
      label: "Companies",
      icon: <BusinessIcon />,
    },
    {
      id: "terminations",
      label: "Terminations",
      icon: <TerminationIcon />,
    },
    {
      id: "whitelabels",
      label: "Whitelabels",
      icon: <WhitelabelIcon />,
    },
    {
      id: "audiences",
      label: "Audiences",
      icon: <AudienceIcon />,
    },
    {
      id: "mailbox-orders",
      label: "Mailbox Orders",
      icon: <EmailIcon />,
    },
    {
      id: "notifications",
      label: "Notifications",
      icon: <NotificationsIcon />,
    },
    {
      id: "feature-flags",
      label: "Feature Flags",
      icon: <FlagIcon />,
    },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "analytics":
        return <AdminAnalytics getToken={getToken} />;
      case "costs":
        return <CostManagement getToken={getToken} />;
      case "users":
        return <UserManagement getToken={getToken} />;
      case "companies":
        return <CompanyManagement getToken={getToken} />;
      case "terminations":
        return <CompanyTerminations getToken={getToken} />;
      case "whitelabels":
        return <WhitelabelManagement getToken={getToken} />;
      case "audiences":
        return <AudienceManagement getToken={getToken} />;
      case "mailbox-orders":
        return <MailboxOrderManagement getToken={getToken} />;
      case "notifications":
        return <NotificationManagement getToken={getToken} />;
      case "feature-flags":
        return <FeatureFlagManagement getToken={getToken} />;
      default:
        return <AdminAnalytics getToken={getToken} />;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <AdminIcon sx={{ fontSize: 32, color: "primary.main" }} />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Admin
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 3,
        }}
      >
        {/* Left Menu */}
        <Box
          sx={{
            width: isMobile ? "100%" : 200,
            flexShrink: 0,
          }}
        >
          <List disablePadding>
            {menuItems.map((item) => (
              <ListItemButton
                key={item.id}
                selected={activeSection === item.id}
                onClick={() => setActiveSection(item.id)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Content Area */}
        <Box sx={{ flex: 1, minWidth: 0 }}>{renderContent()}</Box>
      </Box>
    </Box>
  );
};

export default AdminPage;
